/**
 * O painel de resultados da busca (spec 2026-10-20-kb-busca, Tela). Pastas que casam pelo
 * nome, depois documentos com até 3 trechos; o trecho que só se parece com a busca leva o
 * rótulo "Relacionado". "Perguntar ao @Atendente" aparece sempre, embaixo: juntar, contar e filtrar
 * por data é do @Atendente, não da busca.
 */
import { useState } from 'react'
import { Alert, Badge, Button, Group, Skeleton, Stack, Text, Title, UnstyledButton } from '@mantine/core'
import { IconAlertTriangle, IconFileText, IconFolder, IconFolderOff, IconMessageCircle } from '@tabler/icons-react'
import type { KbBusca, KbBuscaDocumento, KbFolderNode } from '@/api/kb'
import { destacar } from '@/lib/dobrarAcento'
import { mensagemDeErro } from './acesso'
import { MINIMO_SERVIDOR, termosDaBusca } from './busca'
import { STATUS_DO_DOCUMENTO } from './formato'

function Destacado({ texto, termos }: { texto: string; termos: string[] }) {
  return (
    <>
      {destacar(texto, termos).map((p, i) =>
        p.marcado
          ? <mark key={i} style={{ background: 'var(--mantine-color-inovareTeal-0)', color: 'inherit', fontWeight: 600 }}>{p.texto}</mark>
          : <span key={i}>{p.texto}</span>,
      )}
    </>
  )
}

interface Props {
  q: string
  pastas: { no: KbFolderNode; caminho: string }[]
  dados: KbBusca | undefined
  carregando: boolean
  erro: unknown
  pastaExiste: (id: number) => boolean
  onAbrirPasta: (id: number) => void
  onAbrirDocumento: (d: KbBuscaDocumento, pagina: number | null) => void
  onPerguntarAoAtendente: (q: string) => void
}

export function ResultadosDaBusca(p: Props) {
  const termos = termosDaBusca(p.q)
  const [pastaSumida, setPastaSumida] = useState(false)
  const docs = p.dados?.documentos ?? []
  const curta = p.q.trim().length < MINIMO_SERVIDOR

  const abrir = (d: KbBuscaDocumento, pagina: number | null) => {
    if (d.pasta_id !== null && !p.pastaExiste(d.pasta_id)) {
      setPastaSumida(true)
      return
    }
    setPastaSumida(false)
    p.onAbrirDocumento(d, pagina)
  }

  const nada = !curta && !p.carregando && !p.erro && p.dados && docs.length === 0 && p.pastas.length === 0

  return (
    <Stack gap="md">
      <Title order={4}>Resultados para “{p.q.trim()}”</Title>

      {pastaSumida && (
        <Alert color="yellow" icon={<IconFolderOff size={16} />}>Pasta não encontrada.</Alert>
      )}

      {p.pastas.length > 0 && (
        <Stack gap={4}>
          <Text size="xs" fw={600} c="dimmed" tt="uppercase">Pastas</Text>
          {p.pastas.map(({ no, caminho }) => (
            <UnstyledButton key={no.id} onClick={() => p.onAbrirPasta(no.id)}>
              <Group gap="xs" wrap="nowrap">
                <IconFolder size={16} stroke={1.5} />
                <Text size="sm"><Destacado texto={caminho} termos={termos} /></Text>
              </Group>
            </UnstyledButton>
          ))}
        </Stack>
      )}

      {curta ? (
        <Text size="sm" c="dimmed">Digite pelo menos {MINIMO_SERVIDOR} letras para buscar também dentro dos documentos.</Text>
      ) : p.carregando ? (
        <Stack gap="xs"><Skeleton height={48} /><Skeleton height={48} /></Stack>
      ) : p.erro ? (
        <Alert color="red" icon={<IconAlertTriangle size={16} />}>{mensagemDeErro(p.erro, 'A busca não respondeu agora. Tente de novo em instantes.')}</Alert>
      ) : (
        <Stack gap="sm">
          {p.dados?.conteudo === 'indisponivel' && (
            <Alert color="yellow" icon={<IconAlertTriangle size={16} />} p="xs">
              A busca no conteúdo não respondeu agora; mostrando só os nomes.
            </Alert>
          )}
          {docs.length > 0 && <Text size="xs" fw={600} c="dimmed" tt="uppercase">Documentos</Text>}
          {docs.map((d) => {
            const st = STATUS_DO_DOCUMENTO[d.status]
            return (
              <Stack key={d.document_id} gap={4}>
                <UnstyledButton onClick={() => abrir(d, null)} aria-label={`Abrir ${d.titulo}`}>
                  <Group gap="xs" wrap="nowrap">
                    <IconFileText size={16} stroke={1.5} />
                    <Text size="sm" fw={600}><Destacado texto={d.titulo} termos={termos} /></Text>
                    {d.status !== 'indexed' && st && <Badge size="sm" variant="light" color={st.color}>{st.label}</Badge>}
                  </Group>
                </UnstyledButton>
                {d.pasta_caminho && <Text size="xs" c="dimmed">{d.pasta_caminho}</Text>}
                {d.trechos.map((t, i) => (
                  <UnstyledButton key={i} onClick={() => abrir(d, t.pagina)} pl="lg">
                    <Text size="sm">
                      {t.pagina !== null && <Text span size="xs" c="dimmed" ff="monospace">p. {t.pagina} · </Text>}
                      <Destacado texto={t.texto} termos={termos} />
                      {!t.contem_termo && <Badge ml="xs" size="xs" variant="outline" color="gray">Relacionado</Badge>}
                    </Text>
                  </UnstyledButton>
                ))}
              </Stack>
            )
          })}
        </Stack>
      )}

      {nada && <Text size="sm" c="dimmed">Nada encontrado com “{p.q.trim()}”. O @Atendente pode procurar de outro jeito.</Text>}

      {/* Embaixo dos resultados: primeiro a pessoa vê o que a busca achou. */}
      <Group gap="sm">
        <Text size="sm" c="dimmed">Quer uma resposta, e não só os documentos?</Text>
        <Button size="xs" variant="default" leftSection={<IconMessageCircle size={14} stroke={1.5} />}
          onClick={() => p.onPerguntarAoAtendente(p.q.trim())}>
          Perguntar ao @Atendente
        </Button>
      </Group>
    </Stack>
  )
}
